import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Sparkle } from 'lucide-react';
import { usePricing } from '../hooks/usePricing';
import { useCheckoutUrl } from '../hooks/useCheckoutUrl';
import { useCheckoutHandler } from '../hooks/useCheckoutHandler';
export function HeroSection() {
  const {
    checkoutUrl,
    handleCheckout
  } = useCheckoutHandler();
  const {
    formattedPrice,
    isLoading
  } = usePricing();
  const profilePhotos = ['https://cdn.magicpatterns.com/uploads/kS8Z5ZakRrv9TyS6pyQJor/d40244aa3f0ef852d37e20e7d8d6893d.jpg', 'https://cdn.magicpatterns.com/uploads/tq8AsaXoGdsFDCkraAj888/fotoag2015museo.jpg', 'https://cdn.magicpatterns.com/uploads/sMekjXofgHxYiK6qbtAQYq/images_(2)_3_11zon.jpg'];
  return <section className="relative pt-8 pb-12 md:pt-12 md:pb-20 overflow-hidden bg-warm-beige" data-id="element-176">
      <div className="absolute top-0 right-0 w-1/3 h-full bg-copper/5 blur-3xl rounded-full -translate-y-1/2 translate-x-1/4" data-id="element-177" />
      <div className="absolute bottom-0 left-0 w-1/4 h-2/3 bg-copper/5 blur-3xl rounded-full translate-y-1/4 -translate-x-1/4" data-id="element-178" />

      <div className="max-w-5xl mx-auto px-4 relative z-10" data-id="element-179">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-180">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-deep-brown leading-tight mb-6" data-id="element-181">
            Turn Your Pet Into a{' '}
            <span className="text-terracotta italic border-b-2 border-terracotta/60 pb-0.5" data-id="element-182">
              Forever Amigurumi
            </span>
          </h1>

          <p className="text-lg md:text-xl text-gray-600 mb-0 leading-relaxed max-w-2xl mx-auto" data-id="element-183">
            This is not just a pattern. It is your chance to crochet a keepsake
            that looks{' '}
            <span className="font-bold text-terracotta" data-id="element-184">
              exactly like your best friend
            </span>
            — capturing their spirit in every stitch.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="relative max-w-3xl mx-auto mb-4" data-id="element-185">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white p-2 md:p-3" data-id="element-186">
            <div className="relative" data-id="element-187">
              <img src="https://cdn.magicpatterns.com/uploads/njYLEmLyW4kqydiKCQMP29/20260225_1747_Image_Generation_simple_compose_01kjb8vcmxecnb2ptf3s7evtje.png" alt="Real French Bulldog next to its handmade crochet amigurumi version" className="w-full rounded-xl" data-id="element-188" />
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full text-xs font-bold text-deep-brown shadow-md" data-id="element-189">
                Your Pet
              </div>
              <div className="absolute top-4 right-4 bg-deep-brown/90 backdrop-blur-sm px-4 py-1.5 rounded-full text-xs font-bold text-white shadow-md flex items-center gap-1.5" data-id="element-190">
                Your Creation{' '}
                <Heart size={11} fill="currentColor" className="text-copper" data-id="element-191" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Review bar — below image, not overlapping */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1,
        duration: 0.5
      }} className="max-w-3xl mx-auto mb-8" data-id="element-192">
          <div className="bg-white rounded-xl shadow-md px-4 py-3 flex items-center justify-center gap-3" data-id="element-193">
            <div className="text-amber-400 flex text-sm" data-id="element-194">★★★★★</div>
            <p className="text-xs md:text-sm text-gray-600 italic" data-id="element-195">
              "I cried when I finished crocheting my Toby. It looks just like
              him."
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.5
      }} className="flex flex-col items-center gap-5" data-id="element-196">
          <a href={checkoutUrl} onClick={handleCheckout} className="btn-pulse group relative px-10 py-4 bg-copper text-white rounded-full font-bold text-lg shadow-lg hover:shadow-xl hover:bg-copper/90 transition-all duration-300 flex items-center gap-2" data-id="element-197">
            Get My Patterns
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-198" />
          </a>

          <p className="text-sm text-gray-500 italic text-center max-w-md leading-relaxed" data-id="element-199">
            Perfect for personal projects, heartfelt gifts… or turning your
            talent into income with custom pet amigurumis.
          </p>

          <span className="text-copper font-medium text-sm tracking-wide flex items-center gap-1.5" data-id="element-200">
            <Sparkle size={14} className="text-terracotta" data-id="element-201" />
            Digital Ebook – Instant Download
          </span>

          <div className="flex items-center gap-2 text-sm text-warm-brown font-medium" data-id="element-202">
            <div className="flex -space-x-2" data-id="element-203">
              {profilePhotos.map((photo, i) => <img key={i} src={photo} alt={`Happy crafter ${i + 1}`} className="w-8 h-8 rounded-full object-cover border-2 border-white" data-id="element-204" />)}
            </div>
            <span data-id="element-205">+1,200 happy crafters</span>
          </div>
        </motion.div>
      </div>
    </section>;
}